import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Dialog, Menu, MenuItem, Typography } from "@mui/material";
import { AlertTriangle, CheckCircle2, ChevronDown, Plus, Search, X, XCircle } from "lucide-react";
import AppButton from "../../../common_components/AppButton";
import { Dropdown, TextInput } from "../../../common_components/FormInputs";
/**
 * Report Builder — Create / edit custom metric (approved design).
 *
 * A custom metric is a formula over the report's own columns, applied by the
 * API after aggregation (services/reportBuilder/customMetrics.ts). The formula
 * is built from tokens — metric chips, operators, numbers, avg( ) — and sent
 * as the API's string form: `{shopify.total_sales} / ({meta.spend} + 2)`.
 *
 * Grammar mirrors the backend parser: numbers, {key}, + - * / ( ), avg( … ).
 * Division by zero is 0 there (legacy behaviour), which the warning says.
 */
const PINK = "#FF008E";
const CHIP_COLORS = {
    meta: { bg: "#EFF4FF", border: "#B2CCFF", dot: "#1877F2" },
    google_ads: { bg: "#FEF6E7", border: "#FEDF89", dot: "#E8710A" },
    google_analytics: { bg: "#FEF6E7", border: "#FEDF89", dot: "#F9AB00" },
    shopify: { bg: "#ECFDF3", border: "#ABEFC6", dot: "#5E8E3E" },
    email: { bg: "#F4F3FF", border: "#D9D6FE", dot: "#7C3AED" },
    awin: { bg: "#FDF2F7", border: "#F8C9DD", dot: "#C2185B" },
    impact: { bg: "#FEF3F2", border: "#FECDCA", dot: "#F53541" },
    avantlink: { bg: "#F0FDF9", border: "#99F6E0", dot: "#0E9384" },
    cj: { bg: "#F5F6FA", border: "#C7CBE6", dot: "#3E4784" },
    commission_factory: { bg: "#FDF2FA", border: "#FCCEEE", dot: "#EE46BC" },
    costingtons: { bg: "#FFFAEB", border: "#FEDF89", dot: "#A15C07" },
    levanta: { bg: "#ECFDF3", border: "#ABEFC6", dot: "#039855" },
    pepperjam: { bg: "#FEF6EE", border: "#F9DBAF", dot: "#EF6820" },
    rakuten: { bg: "#FEF3F2", border: "#FECDCA", dot: "#B42318" },
};
const CUSTOM_CHIP = { bg: "#FDF2FA", border: "#FCCEEE", dot: PINK };
const FORMATS = [
    { value: "numeric", label: "Number" },
    { value: "currency", label: "Currency" },
    { value: "percentage", label: "Percent" },
];
const OP_LABEL = { "+": "+", "-": "−", "*": "×", "/": "÷", "(": "(", ")": ")" };
// ── Formula string ⇄ tokens ─────────────────────────────────────────────
const toFormula = (tokens) => tokens
    .map((tok) => (tok.t === "ref" ? `{${tok.key}}` : tok.t === "avg" ? "avg" : tok.v))
    .join(" ")
    .replace(/avg \(/g, "avg(")
    .replace(/\( /g, "(")
    .replace(/ \)/g, ")");
const fromFormula = (formula) => {
    const tokens = [];
    let i = 0;
    while (i < formula.length) {
        const c = formula[i];
        if (/\s/.test(c)) {
            i++;
        }
        else if (c === "{") {
            const end = formula.indexOf("}", i);
            if (end < 0)
                break;
            tokens.push({ t: "ref", key: formula.slice(i + 1, end).trim() });
            i = end + 1;
        }
        else if (/[0-9.]/.test(c)) {
            let j = i;
            while (j < formula.length && /[0-9.]/.test(formula[j]))
                j++;
            tokens.push({ t: "num", v: formula.slice(i, j) });
            i = j;
        }
        else if (formula.slice(i, i + 3).toLowerCase() === "avg") {
            tokens.push({ t: "avg" });
            i += 3;
        }
        else if ("+-*/()".includes(c)) {
            tokens.push({ t: "op", v: c });
            i++;
        }
        else {
            i++;
        }
    }
    return tokens;
};
/** Same grammar as the backend parser; the first problem found, or null. */
const validate = (tokens) => {
    if (tokens.length === 0)
        return "Add a metric to start the formula.";
    if (!tokens.some((tok) => tok.t === "ref"))
        return "Use at least one metric.";
    let depth = 0;
    let expectOperand = true;
    for (let i = 0; i < tokens.length; i++) {
        const tok = tokens[i];
        if (expectOperand) {
            if (tok.t === "ref")
                expectOperand = false;
            else if (tok.t === "num") {
                if (!/^\d+(\.\d+)?$|^\.\d+$/.test(tok.v))
                    return `"${tok.v}" is not a number.`;
                expectOperand = false;
            }
            else if (tok.t === "avg") {
                const next = tokens[i + 1];
                if (!next || next.t !== "op" || next.v !== "(")
                    return "avg must be followed by ( … ).";
            }
            else if (tok.v === "(")
                depth++;
            else if (tok.v === "-") {
                // unary minus
            }
            else
                return `Expected a metric or number before "${OP_LABEL[tok.v]}".`;
        }
        else {
            if (tok.t === "op" && tok.v === ")") {
                depth--;
                if (depth < 0)
                    return 'A ")" has no matching "(".';
            }
            else if (tok.t === "op" && tok.v !== "(") {
                expectOperand = true;
            }
            else
                return "Add an operator between two values.";
        }
    }
    if (expectOperand)
        return "The formula ends with an operator.";
    if (depth > 0)
        return 'A "(" is never closed.';
    return null;
};
/** A divisor that is a metric can be zero on some rows. */
const hasMetricDivisor = (tokens) => tokens.some((tok, i) => {
    if (tok.t !== "op" || tok.v !== "/")
        return false;
    const next = tokens[i + 1];
    return next && (next.t === "ref" || next.t === "avg" || (next.t === "op" && next.v === "("));
});
const slug = (name) => name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || "metric";
// ── Small pieces ─────────────────────────────────────────────────────────
const FieldLabel = ({ children, required, optional, }) => (_jsxs(Typography, { sx: { fontSize: "13px !important", color: "#344054", fontWeight: 500, mb: 0.75 }, children: [children, required && _jsx(Box, { component: "span", sx: { color: "#D92D20" }, children: " *" }), optional && _jsx(Box, { component: "span", sx: { color: "#667085", fontWeight: 400 }, children: " (optional)" })] }));
const KeyButton = ({ onClick, children, label, wide, }) => (_jsx(Box, { component: "button", type: "button", "aria-label": label, onClick: onClick, sx: {
        minWidth: wide ? "auto" : 40,
        height: 36,
        px: wide ? 1.25 : 0,
        borderRadius: "8px",
        border: "1px solid #D0D5DD",
        bgcolor: "#fff",
        color: "#344054",
        fontFamily: "inherit",
        fontSize: wide ? "12px" : "14px",
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        cursor: "pointer",
        transition: "border-color .15s ease, background-color .15s ease",
        "&:hover": { borderColor: "#98A2B3", bgcolor: "#F9FAFB" },
        "&:focus-visible": { outline: "none", boxShadow: "0 0 0 2px rgba(255,0,142,0.25)" },
    }, children: children }));
// ── Dialog ───────────────────────────────────────────────────────────────
const CustomMetricDialog = ({ open, onClose, onSave, metrics, positions, takenNames, takenKeys, initial = null, breakdownLabel, saving = false, }) => {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [format, setFormat] = useState("numeric");
    const [position, setPosition] = useState("");
    const [tokens, setTokens] = useState([]);
    const [search, setSearch] = useState("");
    const [touched, setTouched] = useState(false);
    const [fnAnchor, setFnAnchor] = useState(null);
    const editorRef = useRef(null);
    // Fresh form on every open; an edit starts from the saved metric.
    useEffect(() => {
        if (!open)
            return;
        setName(initial?.name || "");
        setDescription(initial?.description || "");
        setFormat(initial?.format || "numeric");
        setPosition(initial?.position || positions[positions.length - 1]?.value || "");
        setTokens(initial ? fromFormula(initial.formula) : []);
        setSearch("");
        setTouched(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const metricByKey = useMemo(() => new Map(metrics.map((m) => [m.key, m])), [metrics]);
    const push = (tok) => {
        setTokens((prev) => [...prev, tok]);
        editorRef.current?.focus();
    };
    /** Digits extend a number that is being typed, otherwise start one. */
    const typeDigit = (digit) => setTokens((prev) => {
        const last = prev[prev.length - 1];
        if (last?.t === "num")
            return [...prev.slice(0, -1), { t: "num", v: last.v + digit }];
        return [...prev, { t: "num", v: digit }];
    });
    const backspace = () => setTokens((prev) => {
        const last = prev[prev.length - 1];
        if (last?.t === "num" && last.v.length > 1)
            return [...prev.slice(0, -1), { t: "num", v: last.v.slice(0, -1) }];
        return prev.slice(0, -1);
    });
    const onEditorKey = (e) => {
        if (/^[0-9.]$/.test(e.key))
            typeDigit(e.key);
        else if ("+-*/()".includes(e.key) && e.key.length === 1)
            push({ t: "op", v: e.key });
        else if (e.key === "x" || e.key === "X")
            push({ t: "op", v: "*" });
        else if (e.key === "Backspace")
            backspace();
        else
            return;
        e.preventDefault();
    };
    const error = validate(tokens);
    const trimmedName = name.trim();
    const nameError = !trimmedName
        ? "Name the metric."
        : takenNames.some((n) => n.toLowerCase() === trimmedName.toLowerCase())
            ? "A column with this name already exists."
            : null;
    const canSave = !error && !nameError && Boolean(position);
    const readable = tokens
        .map((tok) => tok.t === "ref" ? metricByKey.get(tok.key)?.label || tok.key : tok.t === "avg" ? "avg" : tok.t === "op" ? OP_LABEL[tok.v] : tok.v)
        .join(" ")
        .replace(/avg \(/g, "avg(")
        .replace(/\( /g, "(")
        .replace(/ \)/g, ")");
    const save = () => {
        setTouched(true);
        if (!canSave || saving)
            return;
        let key = initial?.key;
        if (!key) {
            const base = `custom_${slug(trimmedName)}`;
            key = base;
            for (let n = 2; takenKeys.includes(key); n++)
                key = `${base}_${n}`;
        }
        onSave({
            key,
            name: trimmedName,
            description: description.trim(),
            format,
            position,
            formula: toFormula(tokens),
        });
    };
    const groups = useMemo(() => {
        const q = search.trim().toLowerCase();
        const map = new Map();
        metrics
            .filter((m) => !q || m.label.toLowerCase().includes(q))
            .forEach((m) => map.set(m.group, [...(map.get(m.group) || []), m]));
        return Array.from(map.entries());
    }, [metrics, search]);
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: false, PaperProps: { sx: { width: 900, maxWidth: "calc(100vw - 32px)", borderRadius: "16px", overflow: "hidden" } }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "flex-start", gap: 2, px: 3, pt: 2.5, pb: 2, borderBottom: "1px solid #EAECF0" }, children: [_jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { sx: { fontSize: "16px !important", fontWeight: 700, color: "#101828" }, children: initial ? "Edit Custom Metric" : "Create Custom Metric" }), _jsx(Typography, { sx: { fontSize: "13px !important", color: "#475467" }, children: "Combine existing metrics into a new column for this report." })] }), _jsx(Box, { component: "button", type: "button", "aria-label": "Close", onClick: onClose, sx: {
                            width: 32,
                            height: 32,
                            border: "none",
                            borderRadius: "8px",
                            bgcolor: "transparent",
                            color: "#475467",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "&:hover": { bgcolor: "#F2F4F7" },
                        }, children: _jsx(X, { size: 18 }) })] }), _jsxs(Box, { sx: { display: "flex", minHeight: 0, maxHeight: "calc(100vh - 220px)" }, children: [_jsxs(Box, { sx: { flex: 1, minWidth: 0, p: 2, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1 }, children: [_jsxs(Box, { sx: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }, children: [_jsxs(Box, { children: [_jsx(FieldLabel, { required: true, children: "Metric name" }), _jsx(TextInput, { size: "sm", value: name, placeholder: "Blended ROAS", onChange: (e) => setName(e.target.value), error: Boolean(touched && nameError), errorMessage: nameError || undefined })] }), _jsxs(Box, { children: [_jsx(FieldLabel, { optional: true, children: "Description" }), _jsx(TextInput, { size: "sm", value: description, placeholder: "Shopify revenue per $1 of total ad spend", onChange: (e) => setDescription(e.target.value) })] }), _jsxs(Box, { children: [_jsx(FieldLabel, { children: "Format" }), _jsx(Box, { role: "radiogroup", sx: { display: "flex", gap: 0.5, p: 0.5, bgcolor: "#F2F4F7", borderRadius: "8px" }, children: FORMATS.map((f) => {
                                                    const on = f.value === format;
                                                    return (_jsx(Box, { component: "button", type: "button", role: "radio", "aria-checked": on, onClick: () => setFormat(f.value), sx: {
                                                            flex: 1,
                                                            height: 32,
                                                            border: "none",
                                                            borderRadius: "6px",
                                                            bgcolor: on ? "#fff" : "transparent",
                                                            boxShadow: on ? "0 1px 2px rgba(16,24,40,0.08)" : "none",
                                                            color: on ? "#101828" : "#475467",
                                                            fontFamily: "inherit",
                                                            fontSize: "12px",
                                                            fontWeight: on ? 600 : 500,
                                                            cursor: "pointer",
                                                            transition: "background-color .15s ease, color .15s ease",
                                                        }, children: f.label }, f.value));
                                                }) })] }), _jsxs(Box, { children: [_jsx(FieldLabel, { children: "Column position" }), _jsx(Dropdown, { size: "sm", usePortal: true, sortByLabel: false, value: position, options: positions.map((p) => ({ value: p.value, label: `After “${p.label}”` })), placeholder: "Pick a column", onChange: (value) => setPosition(value) })] })] }), _jsxs(Box, { children: [_jsxs(Box, { sx: { display: "flex", alignItems: "baseline", mb: 0.75 }, children: [_jsx(Box, { sx: { flex: 1 }, children: _jsx(FieldLabel, { required: true, children: "Formula" }) }), _jsx(Typography, { sx: { fontSize: "12px !important", color: "#667085" }, children: "Click metrics on the right or type to insert" })] }), _jsxs(Box, { ref: editorRef, tabIndex: 0, role: "textbox", "aria-label": "Formula", onKeyDown: onEditorKey, sx: {
                                            minHeight: 60,
                                            p: 1.25,
                                            borderRadius: "8px",
                                            border: `1.5px solid ${touched && error ? "#FDA29B" : PINK}`,
                                            bgcolor: "#fff",
                                            display: "flex",
                                            flexWrap: "wrap",
                                            alignItems: "center",
                                            gap: 0.75,
                                            cursor: "text",
                                            outline: "none",
                                            "&:focus": { boxShadow: "0 0 0 3px rgba(255,0,142,0.12)" },
                                        }, children: [tokens.length === 0 && (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.75 }, children: [_jsx(Box, { sx: { width: "1.5px", height: 18, bgcolor: PINK, animation: "cmBlink 1s step-end infinite", "@keyframes cmBlink": { "50%": { opacity: 0 } } } }), _jsx(Typography, { sx: { fontSize: "13px !important", color: "#98A2B3", fontStyle: "italic", userSelect: "none" }, children: "Click a metric on the right, or type a number / operator" })] })), tokens.map((tok, i) => {
                                                if (tok.t === "ref") {
                                                    const m = metricByKey.get(tok.key);
                                                    const c = (m?.networkKey && CHIP_COLORS[m.networkKey]) || CUSTOM_CHIP;
                                                    return (_jsxs(Box, { sx: {
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 0.75,
                                                            height: 30,
                                                            px: 1,
                                                            borderRadius: "6px",
                                                            bgcolor: c.bg,
                                                            border: `1px solid ${c.border}`,
                                                            fontSize: "12px",
                                                            fontWeight: 500,
                                                            color: "#101828",
                                                        }, children: [_jsx(Box, { sx: { width: 6, height: 6, borderRadius: "50%", bgcolor: c.dot } }), m?.label || tok.key] }, i));
                                                }
                                                return (_jsx(Typography, { component: "span", sx: { fontSize: "14px !important", fontWeight: 600, color: "#344054", px: 0.25 }, children: tok.t === "avg" ? "avg" : tok.t === "op" ? OP_LABEL[tok.v] : tok.v }, i));
                                            }), tokens.length > 0 && (_jsx(Box, { sx: { width: "1.5px", height: 18, bgcolor: PINK, animation: "cmBlink 1s step-end infinite", "@keyframes cmBlink": { "50%": { opacity: 0 } } } }))] }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.75, mt: 1.25, flexWrap: "wrap" }, children: [["+", "-", "*", "/", "(", ")"].map((op) => (_jsx(KeyButton, { label: op, onClick: () => push({ t: "op", v: op }), children: OP_LABEL[op] }, op))), _jsxs(KeyButton, { wide: true, label: "Functions", onClick: (e) => setFnAnchor(e.currentTarget), children: ["Functions ", _jsx(ChevronDown, { size: 13 })] }), _jsx(Menu, { anchorEl: fnAnchor, open: Boolean(fnAnchor), onClose: () => setFnAnchor(null), children: _jsx(MenuItem, { onClick: () => {
                                                        setFnAnchor(null);
                                                        setTokens((prev) => [...prev, { t: "avg" }, { t: "op", v: "(" }]);
                                                        editorRef.current?.focus();
                                                    }, children: _jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: "13px !important", fontWeight: 600 }, children: "avg( \u2026 )" }), _jsx(Typography, { sx: { fontSize: "12px !important", color: "#667085" }, children: "Daily average over the whole period" })] }) }) }), _jsx(Box, { sx: { flex: 1 } }), _jsx(Box, { component: "button", type: "button", onClick: () => setTokens([]), sx: { border: "none", bgcolor: "transparent", color: "#475467", fontFamily: "inherit", fontSize: "13px", fontWeight: 600, cursor: "pointer", "&:hover": { color: "#101828" } }, children: "Clear formula" })] })] }), tokens.length > 0 &&
                                (error ? (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.75, color: "#B42318" }, children: [_jsx(XCircle, { size: 15 }), _jsx(Typography, { sx: { fontSize: "13px !important", color: "#B42318" }, children: error })] })) : (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.75, color: "#067647" }, children: [_jsx(CheckCircle2, { size: 15 }), _jsx(Typography, { sx: { fontSize: "13px !important", color: "#067647" }, children: "Formula is valid" })] }))), !error && hasMetricDivisor(tokens) && (_jsxs(Box, { sx: { display: "flex", gap: 1, p: 1.25, borderRadius: "8px", bgcolor: "#FFFAEB", border: "1px solid #FEDF89" }, children: [_jsx(Box, { sx: { color: "#DC6803", display: "flex", pt: 0.25 }, children: _jsx(AlertTriangle, { size: 15 }) }), _jsxs(Typography, { sx: { fontSize: "13px !important", color: "#93370D" }, children: [_jsx(Box, { component: "span", sx: { fontWeight: 600 }, children: "Division by zero may occur" }), " on rows where the divisor is 0. Those cells will show 0."] })] })), tokens.length > 0 && !error && (_jsxs(Box, { sx: { p: 2, borderRadius: "10px", bgcolor: "#F9FAFB", border: "1px solid #EAECF0" }, children: [_jsx(Typography, { sx: { fontSize: "12px !important", color: "#475467", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", mb: 1 }, children: "Preview" }), _jsxs(Box, { sx: { display: "flex", alignItems: "flex-start", gap: 2 }, children: [_jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { sx: { fontSize: "14px !important", fontWeight: 700, color: "#101828" }, children: trimmedName || "Untitled metric" }), _jsx(Typography, { sx: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "12px !important", color: "#475467", wordBreak: "break-word" }, children: readable || "-" })] }), _jsxs(Box, { sx: { textAlign: "right" }, children: [_jsx(Typography, { sx: { fontSize: "18px !important", fontWeight: 700, color: "#98A2B3" }, children: "-" }), _jsx(Typography, { sx: { fontSize: "12px !important", color: "#667085" }, children: "Values show once the report is generated" })] })] })] }))] }), _jsxs(Box, { sx: { width: 280, flex: "0 0 280px", borderLeft: "1px solid #EAECF0", bgcolor: "#FCFCFD", display: "flex", flexDirection: "column", minHeight: 0 }, children: [_jsxs(Box, { sx: { p: 2, pb: 1.5, borderBottom: "1px solid #EAECF0" }, children: [_jsx(Typography, { sx: { fontSize: "13px !important", fontWeight: 600, color: "#101828", mb: 1 }, children: "Available metrics" }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, height: 38, px: 1.25, border: "1px solid #D0D5DD", borderRadius: "8px", bgcolor: "#fff" }, children: [_jsx(Search, { size: 15, color: "#667085" }), _jsx(Box, { component: "input", value: search, placeholder: "Search metrics", onChange: (e) => setSearch(e.target.value), sx: { border: "none", outline: "none", flex: 1, minWidth: 0, fontFamily: "inherit", fontSize: "13px", bgcolor: "transparent" } })] })] }), _jsxs(Box, { sx: { flex: 1, overflowY: "auto", py: 1 }, children: [groups.length === 0 && (_jsx(Typography, { sx: { fontSize: "13px !important", color: "#667085", px: 2, py: 1 }, children: metrics.length === 0 ? "Select metrics first - a formula uses the report's columns." : "No metrics match." })), groups.map(([group, items]) => {
                                        const dot = (items[0].networkKey && CHIP_COLORS[items[0].networkKey]?.dot) || PINK;
                                        return (_jsxs(Box, { sx: { mb: 0.5 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.75, px: 2, pt: 1, pb: 0.5 }, children: [_jsx(Box, { sx: { width: 6, height: 6, borderRadius: "50%", bgcolor: dot } }), _jsx(Typography, { sx: { fontSize: "12px !important", fontWeight: 700, color: "#475467", letterSpacing: "0.04em", textTransform: "uppercase" }, children: group })] }), items.map((m) => (_jsxs(Box, { component: "button", type: "button", onClick: () => push({ t: "ref", key: m.key }), sx: {
                                                        width: "100%",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        px: 2,
                                                        py: 0.875,
                                                        border: "none",
                                                        bgcolor: "transparent",
                                                        fontFamily: "inherit",
                                                        textAlign: "left",
                                                        cursor: "pointer",
                                                        "&:hover": { bgcolor: "#F2F4F7" },
                                                        "&:hover .cm-plus": { opacity: 1 },
                                                    }, children: [_jsx(Typography, { sx: { fontSize: "13px !important", flex: 1, minWidth: 0, color: "#101828" }, children: m.label }), _jsx(Box, { className: "cm-plus", sx: { color: PINK, display: "flex", opacity: 0.7 }, children: _jsx(Plus, { size: 14 }) })] }, m.key)))] }, group));
                                    })] })] })] }), _jsxs(Box, { sx: { display: "flex", justifyContent: "flex-end", gap: 1.5, px: 3, py: 2, borderTop: "1px solid #EAECF0" }, children: [_jsx(AppButton, { variant: "outlined", onClick: onClose, sx: { height: 44, px: 2.5, textTransform: "none", fontWeight: 600, borderColor: "#D0D5DD", color: "#344054" }, children: "Cancel" }), _jsx(AppButton, { variant: "contained", onClick: save, loading: saving, disabled: touched && !canSave, sx: { height: 44, px: 2.5, textTransform: "none", fontWeight: 600, bgcolor: PINK, boxShadow: "none", "&:hover": { bgcolor: "#E6007A" }, gap: 1 }, children: "Save Metric" })] })] }));
};
export default CustomMetricDialog;
